import { useMemo } from 'react';

import { useMatter } from '@/hooks/useMatter';
import {
  TreatmentRecordRequestSchema,
  TreatmentRecordTypes,
} from '@/services/api/generated/treatmentRecordRequestsService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';
import { MedicalProvider } from '@/services/types/treatment-types';

import { RequestStatusBadge } from '../request/badges/RequestStatusBadge';
import { getRequestOrigin } from '../request/plan-utils';

interface RequestStatusSummaryProps {
  provider: MedicalProvider;
}

const formatStatusDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatRecordTypes = (
  types: TreatmentRecordTypes[] | undefined,
): string => {
  if (!types || types.length === 0) return 'No records specified';
  if (types.length === 1) return types[0];
  if (types.length <= 3) return types.join(', ');
  return `${types.slice(0, 2).join(', ')} +${types.length - 2} more`;
};

const RequestItem = ({
  request,
}: {
  request: TreatmentRecordRequestSchema;
}) => {
  const origin = getRequestOrigin(request);

  return (
    <div className="flex flex-row gap-1 p-2 bg-white border border-gray-200 rounded-md shadow-sm w-fit items-center">
      <div className="flex flex-row items-center gap-2">
        <span className="text-xs font-medium text-gray-700">
          {formatRecordTypes(request.provided_records)}:
        </span>

        {origin && (
          <span className="text-[10px] text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded whitespace-nowrap">
            {origin.label}
          </span>
        )}

        {request.status && <RequestStatusBadge status={request.status} />}
      </div>

      <span className="text-xs text-gray-500 whitespace-nowrap">
        Last Updated {formatStatusDate(request.modified_at)}
      </span>
    </div>
  );
};

const RequestStatusSummary = ({ provider }: RequestStatusSummaryProps) => {
  const { matterId } = useMatter();

  const { data: requests, isLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsListQuery({
      matterId,
      providerId: provider.id,
    });

  const sortedRequests = useMemo(() => {
    if (!requests) return [];
    return [...requests].sort(
      (a, b) =>
        new Date(b.modified_at).getTime() - new Date(a.modified_at).getTime(),
    );
  }, [requests]);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <div className="h-4 bg-gray-200 rounded w-32 animate-pulse" />
        <div className="flex flex-wrap gap-2">
          <div className="h-8 bg-gray-100 rounded animate-pulse w-32" />
          <div className="h-8 bg-gray-100 rounded animate-pulse w-36" />
          <div className="h-8 bg-gray-100 rounded animate-pulse w-28" />
        </div>
      </div>
    );
  }

  if (!requests || requests.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">No record requests yet</div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-semibold text-gray-700">
        Record Requests ({requests.length})
      </span>

      <div className="flex flex-wrap gap-2">
        {sortedRequests.map((request) => (
          <RequestItem key={request.id} request={request} />
        ))}
      </div>
    </div>
  );
};

export { RequestStatusSummary };
